/**
 * Value Frequency Bar Renderer
 * 
 * Renders horizontal/vertical bar chart with SVG.
 * Supports category labels and value display.
 */

import type { VisualizationRenderer } from '../registry.js';
import { designTokens, escapeHtml } from '../../../core/design-system.js';

export const renderValueFrequencyBar: VisualizationRenderer = (data, meta) => {
  // Expected data format: array of { category: string, count: number } or { value: string, frequency: number }
  if (!Array.isArray(data) || data.length === 0) {
    return renderEmptyState('No data to display');
  }

  // Normalize data format
  const items = data.map(item => {
    const category = item.category !== undefined ? String(item.category) : (item.value !== undefined ? String(item.value) : '');
    const count = item.count !== undefined ? Number(item.count) : (item.frequency !== undefined ? Number(item.frequency) : 0);
    return { category, count };
  });

  // Sort by count (descending) for better visualization
  items.sort((a, b) => b.count - a.count);

  // Calculate dimensions
  const width = 600;
  const barHeight = 30;
  const barSpacing = 10;
  const padding = { top: 20, right: 100, bottom: 20, left: 150 };
  const chartHeight = items.length * (barHeight + barSpacing) + padding.top + padding.bottom;
  const chartWidth = width - padding.left - padding.right;

  // Find max count for scaling
  const maxCount = Math.max(...items.map(i => i.count), 1);

  // Generate bars
  const barElements = items.map((item, index) => {
    const barWidth = (item.count / maxCount) * chartWidth;
    const y = padding.top + (index * (barHeight + barSpacing));
    
    // Truncate long category names
    const displayCategory = item.category.length > 20 
      ? item.category.substring(0, 20) + '...' 
      : item.category;

    return `
      <g>
        <rect
          x="${padding.left}"
          y="${y}"
          width="${barWidth}"
          height="${barHeight}"
          fill="rgba(102, 204, 255, 0.6)"
          stroke="rgba(102, 204, 255, 0.8)"
          stroke-width="1"
          rx="4"
          style="cursor: pointer; transition: fill 0.2s;"
          onmouseenter="this.style.fill = 'rgba(102, 204, 255, 0.9)'"
          onmouseleave="this.style.fill = 'rgba(102, 204, 255, 0.6)'"
        />
        <text
          x="${padding.left - 10}"
          y="${y + barHeight / 2 + 4}"
          text-anchor="end"
          font-size="${designTokens.fontSize.sm}"
          fill="${designTokens.contentColor}"
        >${escapeHtml(displayCategory)}</text>
        <text
          x="${padding.left + barWidth + 10}"
          y="${y + barHeight / 2 + 4}"
          font-size="${designTokens.fontSize.sm}"
          fill="${designTokens.contentColor}"
          font-weight="600"
        >${item.count}</text>
      </g>
    `;
  }).join('');

  const title = meta?.title || 'Value Frequency';
  const units = meta?.units || '';

  return `
    <div style="width: 100%;">
      ${title ? `
        <div style="
          margin-bottom: ${designTokens.padding.md};
          font-size: ${designTokens.fontSize.md};
          font-weight: 600;
          color: ${designTokens.contentColor};
        ">${escapeHtml(title)}</div>
      ` : ''}
      <div style="
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
      ">
        <svg
          width="${width}"
          height="${chartHeight}"
          style="
            display: block;
            max-width: 100%;
            height: auto;
          "
        >
          ${barElements}
        </svg>
      </div>
      ${units ? `
        <div style="
          margin-top: ${designTokens.padding.sm};
          font-size: ${designTokens.fontSize.xs};
          color: ${designTokens.contentColorMuted};
          text-align: center;
        ">Units: ${escapeHtml(units)}</div>
      ` : ''}
    </div>
  `;
};

function renderEmptyState(message: string): string {
  return `
    <div style="
      padding: ${designTokens.padding.xl};
      text-align: center;
      color: ${designTokens.contentColorMuted};
      font-size: ${designTokens.fontSize.sm};
    ">${escapeHtml(message)}</div>
  `;
}


